/**
 * Shared bucket-label formatter for Recharts X axes + tooltips.
 *
 * Renders labels in CET (Europe/Berlin) — SSOT for all UI time display. See
 * lib/format-date.ts for background on the CET switch (user request
 * 2026-04-24). Hourly buckets: "Mon d HH:mm" CET. Daily / weekly: "Mon d".
 *
 * When granularity is 'auto' we infer hourly from whether the bucket's CET
 * hour/minute are non-zero — daily buckets in backend land at UTC midnight
 * which becomes 02:00 CET (or 01:00 under DST), so the old UTC-zero check
 * no longer works. We derive hourly-ness from the source UTC components
 * instead: a daily bucket has `getUTCHours === 0 && getUTCMinutes === 0`.
 */

import { getCetParts } from './format-date.js';

export type BucketGranularity = 'hourly' | 'daily' | 'weekly' | 'auto';

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatBucketLabel(
  bucket: string,
  granularity: BucketGranularity = 'auto',
): string {
  const d = new Date(bucket);
  if (Number.isNaN(d.getTime())) return bucket;

  const parts = getCetParts(d);
  if (!parts) return bucket;

  // Source-data check: hourly buckets have non-zero UTC H/M; daily buckets
  // are at UTC midnight. This decouples the hourly-detection from the CET
  // render (which shifts midnight to 01:00/02:00 depending on DST).
  const isHourly =
    granularity === 'hourly' ||
    (granularity === 'auto' &&
      (d.getUTCHours() !== 0 || d.getUTCMinutes() !== 0));

  const month = MONTH_NAMES[parts.month - 1];
  if (isHourly) {
    const hh = String(parts.hour).padStart(2, '0');
    const mm = String(parts.minute).padStart(2, '0');
    return `${month} ${parts.day} ${hh}:${mm}`;
  }

  return `${month} ${parts.day}`;
}
